/**
 * TomTom Setup Page
 * Allows admins to configure TomTom API settings
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Map, Check, AlertTriangle, Link2, Loader2, Save, Plane, ClipboardList } from 'lucide-react';
import { settingsApi, TomTomSettingsResponse, AviationStackSettingsResponse } from '../services/settingsApi';

const TomTomSetup: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [settings, setSettings] = useState<TomTomSettingsResponse | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // AviationStack state
  const [avStack, setAvStack] = useState<AviationStackSettingsResponse | null>(null);
  const [avApiKey, setAvApiKey] = useState('');
  const [avSaving, setAvSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const [data, avData] = await Promise.all([
        settingsApi.getTomTomSettings(),
        settingsApi.getAviationStackSettings().catch(() => null),
      ]);
      setSettings(data);
      if (avData) {
        setAvStack(avData);
      }
    } catch (error: any) {
      console.error('Failed to load settings:', error);
      if (error.response?.status === 403) {
        setMessage({ type: 'error', text: 'Keine Berechtigung. Nur Administratoren können diese Einstellungen ändern.' });
      } else {
        setMessage({ type: 'error', text: 'Fehler beim Laden der Einstellungen' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAviationStack = async () => {
    try {
      setAvSaving(true);
      setMessage(null);
      const result = await settingsApi.updateAviationStackSettings({
        apiKey: avApiKey || undefined,
      });
      setAvStack(result);
      setAvApiKey('');
      setMessage({ type: 'success', text: 'AviationStack Einstellungen gespeichert!' });
    } catch (error: any) {
      setMessage({ type: 'error', text: 'Fehler beim Speichern der AviationStack Einstellungen' });
    } finally {
      setAvSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      setTestResult(null);

      const data = await settingsApi.updateTomTomSettings({
        apiKey: apiKey || undefined,
      });

      setSettings(data);
      setApiKey(''); // Clear the API key field after save
      setMessage({ type: 'success', text: 'Einstellungen wurden gespeichert!' });
    } catch (error: any) {
      console.error('Failed to save settings:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Fehler beim Speichern' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      setMessage(null);

      const result = await settingsApi.testTomTomConnection();
      setTestResult(result);
    } catch (error: any) {
      console.error('Connection test failed:', error);
      setTestResult({ success: false, message: 'Verbindungstest fehlgeschlagen' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="tomtom-setup-page">
        <div className="loading-spinner">Lade Einstellungen...</div>
      </div>
    );
  }

  return (
    <div className="tomtom-setup-page">
      <div className="setup-container">
        <div className="setup-header">
          <button type="button" className="back-button" onClick={() => navigate(-1)}>
            ← Zurück
          </button>
          <h1><Map size={24} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} /> TomTom Einstellungen</h1>
          <p className="subtitle">Konfigurieren Sie die TomTom API für Routing und Geocoding</p>
        </div>

        {message && (
          <div className={`message ${message.type}`}>
            {message.type === 'success' ? <><Check size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /></> : <><AlertTriangle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /></>} {message.text}
          </div>
        )}

        <div className="setup-form">
          {/* API Key */}
          <div className="form-group">
            <label htmlFor="apiKey">TomTom API Key</label>
            <div className="api-key-input">
              <input
                type="password"
                id="apiKey"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={settings?.hasApiKey ? '••••••••' : 'API Key eingeben'}
              />
              {settings?.hasApiKey && (
                <span className="key-status configured">✓ Konfiguriert: {settings.apiKeyMasked}</span>
              )}
            </div>
            <span className="help-text">
              Den API Key erhalten Sie unter{' '}
              <a href="https://developer.tomtom.com/" target="_blank" rel="noopener noreferrer">
                developer.tomtom.com
              </a>
            </span>
          </div>
          {/* Test Result */}
          {testResult && (
            <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
              {testResult.success ? <><Check size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /></> : '✗'} {testResult.message}
            </div>
          )}

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              type="button"
              className="test-button"
              onClick={handleTest}
              disabled={testing || !settings?.hasApiKey}
            >
              {testing ? <><Loader2 size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Teste...</> : <><Link2 size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Verbindung testen</>}
            </button>
            <button
              type="button"
              className="save-button"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <><Loader2 size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Speichern...</> : <><Save size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Speichern</>}
            </button>
          </div>
        </div>

        {/* AviationStack Section */}
        <div style={{ borderTop: '1px solid #dee2e6', marginTop: '24px', paddingTop: '24px' }}>
          <h2 style={{ fontSize: '20px', color: '#333', margin: '0 0 8px' }}><Plane size={20} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> AviationStack</h2>
          <p style={{ color: '#666', fontSize: '14px', margin: '0 0 16px' }}>
            Für Flug-Tracking wird ein AviationStack API Key benötigt. Unterstützt IATA-Flugnummern direkt (z.B. LH1234).
          </p>

          <div className="form-group">
            <label htmlFor="avApiKey">API Key</label>
            <input
              type="password"
              id="avApiKey"
              value={avApiKey}
              onChange={(e) => setAvApiKey(e.target.value)}
              placeholder={avStack?.hasApiKey ? '••••••••' : 'AviationStack API Key eingeben'}
            />
            {avStack?.hasApiKey && (
              <span className="key-status configured">✓ Konfiguriert: {avStack.apiKeyMasked}</span>
            )}
            <span className="help-text">
              Kostenlosen API Key erstellen unter{' '}
              <a href="https://aviationstack.com/signup/free" target="_blank" rel="noopener noreferrer">
                aviationstack.com
              </a>
            </span>
          </div>

          <button
            type="button"
            className="save-button"
            onClick={handleSaveAviationStack}
            disabled={avSaving}
            style={{ width: '100%' }}
          >
            {avSaving ? <><Loader2 size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Speichern...</> : <><Save size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> AviationStack speichern</>}
          </button>
        </div>

        {/* Info Box */}
        <div className="info-box">
          <h3><ClipboardList size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Anleitung</h3>
          <ol>
            <li>Erstellen Sie einen Account bei <a href="https://developer.tomtom.com/" target="_blank" rel="noopener noreferrer">TomTom Developer Portal</a></li>
            <li>Erstellen Sie einen neuen API Key</li>
            <li>Geben Sie den API Key hier ein</li>
            <li>Testen Sie die Verbindung</li>
          </ol>
        </div>
      </div>

      <style>{`
        .tomtom-setup-page {
          padding: 20px;
          max-width: 700px;
          margin: 0 auto;
        }
        .setup-container {
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          padding: 24px;
        }
        .setup-header {
          margin-bottom: 24px;
        }
        .setup-header h1 {
          margin: 16px 0 8px;
          font-size: 24px;
          color: #333;
        }
        .subtitle {
          color: #666;
          margin: 0;
        }
        .back-button {
          background: none;
          border: none;
          color: #007bff;
          cursor: pointer;
          padding: 4px 0;
          font-size: 14px;
        }
        .back-button:hover {
          text-decoration: underline;
        }
        .message {
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 16px;
        }
        .message.success {
          background: #d4edda;
          color: #155724;
        }
        .message.error {
          background: #f8d7da;
          color: #721c24;
        }
        .form-group {
          margin-bottom: 20px;
        }
        .form-group label {
          display: block;
          font-weight: 600;
          margin-bottom: 6px;
          color: #333;
        }
        .form-group input {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 14px;
          box-sizing: border-box;
        }
        .form-group input:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 3px rgba(0,123,255,0.1);
        }
        .help-text {
          display: block;
          font-size: 12px;
          color: #666;
          margin-top: 4px;
        }
        .help-text a {
          color: #007bff;
        }
        .api-key-input {
          position: relative;
        }
        .key-status {
          display: block;
          font-size: 12px;
          margin-top: 4px;
        }
        .key-status.configured {
          color: #28a745;
        }
        .test-result {
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 16px;
        }
        .test-result.success {
          background: #d4edda;
          color: #155724;
        }
        .test-result.error {
          background: #f8d7da;
          color: #721c24;
        }
        .form-actions {
          display: flex;
          gap: 12px;
          margin-top: 24px;
        }
        .test-button, .save-button {
          padding: 12px 24px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          transition: background-color 0.2s;
        }
        .test-button {
          background: #6c757d;
          color: white;
        }
        .test-button:hover:not(:disabled) {
          background: #545b62;
        }
        .save-button {
          background: #007bff;
          color: white;
          flex: 1;
        }
        .save-button:hover:not(:disabled) {
          background: #0056b3;
        }
        .test-button:disabled, .save-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .info-box {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 16px;
          margin-top: 24px;
        }
        .info-box h3 {
          margin: 0 0 12px;
          font-size: 16px;
          color: #333;
        }
        .info-box ol {
          margin: 0;
          padding-left: 20px;
          color: #666;
        }
        .info-box li {
          margin-bottom: 8px;
        }
        .info-box a {
          color: #007bff;
        }
        .loading-spinner {
          text-align: center;
          padding: 40px;
          color: #666;
        }
      `}</style>
    </div>
  );
};

export default TomTomSetup;

